import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { DashboardService } from '@ld.consumer-portal/data-access';

/**
 * Kicks off dashboard data loading for a route that is about to activate.
 *
 * This is a resolver rather than a guard because it never gates anything — it only
 * triggers a fetch. Resolvers also run in a later pipeline stage than guards
 * (`checkGuards()` → `resolveData()`), so they cannot start until every guard has
 * returned `true`. That ordering matters: guards within a single `canActivate` array
 * are subscribed concurrently, so as a guard this used to fire borrower requests on a
 * lapsed token in parallel with `authGuard`, producing 401s that raced the redirect
 * to login. As a resolver it is never subscribed on a rejected navigation.
 *
 * Returns `null` synchronously so navigation is not delayed; the fetch itself stays
 * fire-and-forget and its errors are surfaced by the component.
 */
export const dashboardDataResolver: ResolveFn<null> = (route: ActivatedRouteSnapshot) => {
  const dashboardService = inject(DashboardService);

  const hasCachedData = dashboardService.loanResponses()?.length && dashboardService.userProfile();

  if (!hasCachedData) {
    const accountId = route.paramMap.get('accountId');
    if (accountId) {
      dashboardService.loadSingleLoanFirst(accountId).catch(() => { /* handled by component */ });
    } else {
      dashboardService.getUserAccounts().subscribe();
    }
  }

  return null;
};
